import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/hooks/useSEO";
import { IMAGES, PROGRAMS, BRAND_VIDEO, MUSIC_VIDEO, CONTACT } from "@/data/brand";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ArrowRight, Building2, GraduationCap, HeartHandshake, Music, Film, CheckCircle2, Sparkles, Phone } from "lucide-react";

const SPONSOR_TIERS = [
  { tier: "Community Partner", price: "$2,500+", perks: ["Logo on program materials", "Recognition at 1 community event", "Quarterly impact report"] },
  { tier: "Program Sponsor", price: "$10,000+", perks: ["Everything in Community", "Named school/daycare program", "Featured on website & press", "Co-branded safety kits"], featured: true },
  { tier: "Founding Sponsor", price: "$25,000+", perks: ["Everything in Program", "Statewide Georgia rollout", "Custom song or animation credit", "Executive briefing with founders"] },
];

const CREATIONS = [
  { q: "Custom Children's Songs", a: "We write and produce original, catchy safety songs tailored to your topic, age group or campaign — the kind of tune kids sing on the way home and remember for years." },
  { q: "Original Animations", a: "From short explainer clips to fully animated characters and stories, we create engaging, kid-friendly animation built around your safety message." },
  { q: "Made-to-Order Content", a: "Need a jingle, a mascot animation, or a themed music video for your organization or event? Tell us your idea and we'll bring it to life — reach out and let's collaborate." },
];

export default function Programs() {
  useSEO({ title: "Programs & Partnerships for Schools & Sponsors", description: "Educational safety programs for schools and daycares, corporate sponsorship opportunities in Georgia, and custom children's songs and animations made to order." });

  return (
    <>
      {/* Hero */}
      <section className="pt-36 pb-16 max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-blue mb-4">Programs & Partnerships</p>
          <h1 className="font-display text-6xl sm:text-8xl tracking-tight leading-[0.85] max-w-4xl">
            Bring safety education to your community.
          </h1>
          <p className="font-body text-lg text-sq-ink/70 mt-6 max-w-2xl">
            The Safety Quest partners with schools, daycares and sponsors to deliver
            localized, high-impact safety programs — starting in Georgia, scaling everywhere.
          </p>
        </Reveal>
      </section>

      {/* Programs grid */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {PROGRAMS.map((p, i) => {
            const icons = [GraduationCap, Building2, HeartHandshake];
            const Icon = icons[i];
            return (
              <Reveal key={p.title} delay={i * 0.1}>
                <div data-testid={`program-${i}`} className="h-full rounded-3xl bg-white border border-sq-ink/10 p-8 hover:shadow-xl transition-shadow">
                  <div className="grid place-items-center w-14 h-14 rounded-2xl bg-sq-blue/10 text-sq-blue mb-6"><Icon size={26} /></div>
                  <span className="text-xs font-bold uppercase tracking-widest text-sq-ink/40">{p.audience}</span>
                  <h3 className="font-round text-2xl font-semibold mt-1">{p.title}</h3>
                  <p className="text-sq-ink/70 mt-3 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15}>
          <div data-testid="live-pricing" className="mt-8 rounded-3xl bg-sq-yellow border-2 border-sq-ink p-8 sm:flex items-center justify-between gap-6 shadow-[0_6px_0_#111827]">
            <div>
              <h3 className="font-round text-2xl font-semibold">Live performances at daycares, schools & public events</h3>
              <p className="text-sq-ink/80 mt-2">
                Bring Bobby, Pluggy and the crew to your event! <b>Basic appearances start at $500.</b> Call for pricing and availability.
              </p>
            </div>
            <a href={CONTACT.phoneHref} data-testid="live-pricing-call" className="mt-4 sm:mt-0 shrink-0 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-sq-red text-white font-bold btn-chunky">
              <Phone size={18} /> Call {CONTACT.phone}
            </a>
          </div>
        </Reveal>
      </section>

      {/* Sponsor pitch — Swiss grid */}
      <section className="bg-sq-ink text-white py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <Reveal className="lg:col-span-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-yellow mb-3">For Corporate & Fiscal Sponsors</p>
              <h2 className="font-display text-5xl lg:text-7xl tracking-tight leading-[0.9]">Fund a safer Georgia.</h2>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-4">
              <p className="text-white/60 leading-relaxed">
                Your sponsorship directly funds localized safety programs in schools and daycares —
                measurable impact, meaningful brand alignment, and a lasting community legacy.
              </p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {SPONSOR_TIERS.map((t, i) => (
              <Reveal key={t.tier} delay={i * 0.08}>
                <div data-testid={`sponsor-tier-${i}`} className={`h-full rounded-3xl p-8 border ${t.featured ? "bg-sq-red border-sq-red" : "bg-white/[0.04] border-white/10"}`}>
                  <h3 className="font-round text-2xl font-semibold">{t.tier}</h3>
                  <p className="font-display text-5xl tracking-wide mt-2">{t.price}</p>
                  <ul className="mt-6 space-y-3">
                    {t.perks.map((p) => (
                      <li key={p} className="flex gap-2 text-sm text-white/80"><CheckCircle2 size={18} className="text-sq-yellow shrink-0" /> {p}</li>
                    ))}
                  </ul>
                  <Link to="/contact" data-testid={`sponsor-cta-${i}`} className={`mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-full font-bold text-sm ${t.featured ? "bg-white text-sq-ink" : "bg-sq-yellow text-sq-ink"}`}>
                    Become a Sponsor <ArrowRight size={16} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsor marquee */}
      <div className="py-8 bg-white border-y border-sq-ink/10">
        <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-sq-ink/40 mb-5">Trusted by partners & communities</p>
        <Marquee speed={30} gradient={false} autoFill>
          {["Georgia Fire Dept", "Head Start GA", "Daycare Alliance", "Safe Kids GA", "Community First", "Atlanta Schools"].map((s, i) => (
            <span key={i} className="mx-10 font-round text-2xl font-semibold text-sq-ink/30">{s}</span>
          ))}
        </Marquee>
      </div>

      {/* Custom Songs & Animations */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <div className="grid place-items-center w-14 h-14 rounded-2xl bg-sq-red text-white mb-5"><Music size={26} /></div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-red mb-3">Custom Content Studio</p>
            <h2 className="font-display text-5xl tracking-tight leading-[0.9]">We create songs & animations</h2>
            <p className="text-sq-ink/70 mt-4">Beyond our own books and music, we produce original children's songs and animations for organizations, campaigns and events. If you're interested in custom content, reach out — we'd love to create something with you.</p>
            <Link to="/contact" data-testid="creations-cta" className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-sq-ink text-white font-bold btn-chunky">
              <Sparkles size={18} /> Start a Custom Project
            </Link>
          </div>
        </Reveal>

        <div className="lg:col-span-8">
          <Accordion type="single" collapsible defaultValue="item-0" className="space-y-4">
            {CREATIONS.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`creation-${i}`} className="border-2 border-sq-ink rounded-2xl px-6 bg-white">
                <AccordionTrigger className="font-round text-xl font-semibold hover:no-underline py-5">
                  <span className="flex items-center gap-3"><Film size={20} className="text-sq-red" /> {item.q}</span>
                </AccordionTrigger>
                <AccordionContent className="text-sq-ink/70 leading-relaxed pb-5">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <Reveal delay={0.1}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-red mt-10 mb-4">A taste of our work</p>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { src: MUSIC_VIDEO, label: "Original Safety Song", testid: "work-song" },
                { src: BRAND_VIDEO, label: "Animated Promo", testid: "work-promo" },
              ].map((v) => (
                <div key={v.testid} data-testid={v.testid} className="rounded-2xl overflow-hidden border-2 border-sq-ink bg-white">
                  <div className="aspect-video bg-black">
                    <video src={v.src} controls playsInline preload="metadata" poster={IMAGES.heroScene} className="w-full h-full object-contain bg-black" />
                  </div>
                  <p className="px-4 py-3 font-round font-semibold text-sm">{v.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
